
import React, { memo } from 'react';
import SectionHeading from '../ui/SectionHeading';
import { formatText } from '@/utils/report';

interface BulletListSectionProps {
  content: string;
  index: number;
}

/**
 * Extracts bullet points from content
 */
function extractBulletPoints(content: string): string[] {
  if (!content) return [];
  
  // Split content into lines
  const lines = content.split('\n');
  
  // Match bullet point patterns
  const bulletPointPattern = /^[-•*]\s+/;
  
  // Filter out lines with bullet points and clean them
  return lines
    .filter(line => bulletPointPattern.test(line.trim()))
    .map(line => line.trim().replace(bulletPointPattern, '').trim());
}

/**
 * Extracts a title from a list section (if present)
 */
function extractListTitle(content: string): string | null {
  if (!content) return null;
  
  // Try to find a title before the bullet points
  const lines = content.split('\n');
  const bulletPointPattern = /^[-•*]\s+/;
  
  // Look for a non-empty line before the first bullet point
  for (let i = 0; i < lines.length; i++) {
    if (bulletPointPattern.test(lines[i].trim())) {
      // If there's a title before the first bullet point, return it
      if (i > 0 && lines[i - 1].trim().length > 0) {
        const titleLine = lines[i - 1].trim();
        // Check if this is a heading and clean it if needed
        if (/^#+\s+/.test(titleLine)) {
          return titleLine.replace(/^#+\s+/, '');
        }
        return titleLine;
      }
      break;
    }
  }
  
  return null;
}

const BulletListSection: React.FC<BulletListSectionProps> = ({ content, index }) => {
  // Extract list title and bullet points
  const sectionTitle = extractListTitle(content);
  const listItems = extractBulletPoints(content);
  
  if (listItems.length === 0) return null;
  
  return (
    <div key={index} className="my-5">
      {sectionTitle && (
        <SectionHeading text={sectionTitle} level="h3" className="mb-3 text-base font-medium" />
      )}
      <ul className="ml-4 list-none space-y-2">
        {listItems.map((item, idx) => (
          <li key={idx} className="flex items-start">
            <span className="text-blue-400 mr-2.5 inline-block mt-1 flex-shrink-0">•</span>
            <span className="text-gray-300" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default memo(BulletListSection);
